import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '../components/ui/sonner';
import { playNotificationSound, setSoundPreference } from '../utils/notificationSound';
import { sendTemplatedNotification, getNotificationPermission } from '../utils/pushNotifications';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [lastMessage, setLastMessage] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    soundEnabled: true,
    soundType: 'chime', // 'chime', 'alert', or 'off'
    pushEnabled: getNotificationPermission() === 'granted',
    types: {
      shift_filled: true,
      new_application: true,
      shift_cancelled: true,
      shift_reminder: true,
      new_message: true,
      payment_received: true
    }
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  // Live data-event subscribers (useLiveRefresh hook) — Set of callbacks.
  const dataListenersRef = useRef(new Set());
  // Exponential-backoff state — UAT flagged "Repeated WebSocket errors every
  // few seconds" because the previous code retried on a fixed 3-second timer.
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10;

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('notificationSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setNotificationSettings(prev => ({ ...prev, ...parsed }));
        setSoundPreference(parsed.soundType || 'chime');
      } catch (e) {
        console.error('Failed to parse notification settings:', e);
      }
    }
  }, []);

  // Update sound preference when settings change
  useEffect(() => {
    if (notificationSettings.soundEnabled) {
      setSoundPreference(notificationSettings.soundType);
    } else {
      setSoundPreference('off');
    }
    localStorage.setItem('notificationSettings', JSON.stringify(notificationSettings));
  }, [notificationSettings]);

  const updateNotificationSettings = useCallback((newSettings) => {
    setNotificationSettings(prev => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem('notificationSettings', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const connect = () => {
    if (!user?._id || wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use the /api/ws/* path so the Kubernetes ingress routes it to the backend.
    // HR sub-users act as their org for API calls but must receive THEIR OWN real-time alerts.
    const wsUrl = `${protocol}//${window.location.host}/api/ws/${user._acting_hr_id || user._id}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        if (process.env.NODE_ENV !== 'production') console.log('✅ WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // reset backoff on success
        // Send heartbeat
        const heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        ws.heartbeat = heartbeat;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'data_event') {
            // App-wide live refresh signal — fan out to subscribed components.
            dataListenersRef.current.forEach((fn) => {
              try { fn(data); } catch (e) { /* listener errors must not break WS */ }
            });
            return;
          }
          setLastMessage(data);
          handleNotification(data);
        } catch (e) {
          // Ignore non-JSON messages — server may send keepalive frames.
          if (process.env.NODE_ENV !== 'production') console.debug('ws non-JSON message:', e?.message);
        }
      };

      ws.onerror = (error) => {
        // Only log in dev — these fire on every reconnect attempt and pollute
        // the console in production. The onclose handler does the recovery.
        if (process.env.NODE_ENV !== 'production') console.debug('WebSocket error:', error?.message || error);
      };

      ws.onclose = () => {
        if (process.env.NODE_ENV !== 'production') console.debug('WebSocket disconnected');
        setIsConnected(false);
        if (ws.heartbeat) clearInterval(ws.heartbeat);

        // Exponential backoff with jitter — caps at MAX attempts to prevent
        // the "every few seconds" reconnect-spam UAT flagged.
        const attempt = reconnectAttemptsRef.current;
        if (attempt >= MAX_RECONNECT_ATTEMPTS) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`WebSocket gave up after ${attempt} reconnect attempts`);
          }
          return;
        }
        // 1s, 2s, 4s, 8s, 16s, 30s cap; +/- 25% jitter to avoid thundering herd.
        const base = Math.min(1000 * Math.pow(2, attempt), 30000);
        const jitter = base * (0.75 + Math.random() * 0.5);
        reconnectAttemptsRef.current = attempt + 1;
        reconnectTimeoutRef.current = setTimeout(connect, jitter);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  };

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  };

  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const handleNotification = (data) => {
    // Don't add pong messages to notifications
    if (data.type === 'pong' || data.type === 'message_sent') return;
    
    setNotifications(prev => [data, ...prev].slice(0, 50)); // Keep last 50

    // Check if this notification type is enabled
    const typeEnabled = notificationSettings.types[data.type] !== false;

    // Play sound if enabled and notification type is enabled
    if (notificationSettings.soundEnabled && typeEnabled) {
      if (data.sound || data.priority === 'high' || 
          ['job_picked', 'shift_filled', 'new_application'].includes(data.type)) {
        playNotificationSound(data.type || 'default');
      }
    }

    // Send browser push notification if enabled
    if (notificationSettings.pushEnabled && typeEnabled && document.visibilityState !== 'visible') {
      sendTemplatedNotification(data.type, data.data || {});
    }

    // Show toast based on notification type
    switch (data.type) {
      case 'job_posted':
        toast.info('New Job Available!', {
          description: data.data?.title || 'Check out the latest job posting',
        });
        break;
      
      case 'shift_filled':
      case 'job_picked':
        toast.success('🎉 Shift Filled!', {
          description: data.data?.message || `${data.data?.worker_name || 'A worker'} accepted your shift`,
          duration: 10000,
        });
        break;
      
      case 'new_application':
        toast.info('📋 New Application', {
          description: data.data?.message || `${data.data?.worker_name || 'A worker'} applied for your shift`,
          duration: 8000,
        });
        break;
      
      case 'shift_cancelled':
        toast.warning('⚠️ Shift Cancelled', {
          description: data.data?.message || 'A worker cancelled their shift',
          duration: 10000,
        });
        break;
      
      case 'shift_reminder':
        toast.info('⏰ Shift Starting Soon', {
          description: data.data?.message || 'Your shift starts in 1 hour',
          duration: 15000,
        });
        break;
      
      case 'job_confirmed':
        toast.success('Shift Confirmed!', {
          description: data.data?.message || 'Your shift has been confirmed',
        });
        break;
      
      case 'payment_received':
        toast.success('💰 Payment Received!', {
          description: data.data?.message || 'Payment deposited to your wallet',
        });
        break;
      
      case 'new_message':
        toast.info('💬 New Message', {
          description: data.sender_name ? `From ${data.sender_name}` : 'You have a new message',
        });
        break;

      case 'worker_checked_in':
        toast.info('🟢 Worker Clocked In', {
          description: data.message || data.data?.worker_name + ' clocked in',
          duration: 8000,
        });
        break;

      case 'worker_checked_out':
        toast.info('🔵 Worker Clocked Out', {
          description: data.message || 'Timesheet ready for review',
          duration: 8000,
        });
        break;

      case 'tasks_all_complete':
        toast.success('✅ Tasks Complete', {
          description: data.message || 'All shift tasks finished',
          duration: 8000,
        });
        break;

      case 'timesheet_acknowledged':
        toast.info('📝 Timesheet Acknowledged', {
          description: data.message || 'Worker acknowledged timesheet',
          duration: 8000,
        });
        break;

      case 'timesheet_edited':
        toast.warning('✏️ Timesheet Updated', {
          description: data.message || 'Please re-acknowledge your timesheet',
          duration: 12000,
        });
        break;

      case 'timesheet_dispute':
        toast.warning('⚠️ Timesheet Disputed', {
          description: data.message || 'A worker flagged a timesheet entry',
          duration: 12000,
        });
        break;

      case 'dispute_resolved':
        toast.info('✓ Dispute Resolved', {
          description: data.message || 'Your timesheet dispute was resolved',
          duration: 10000,
        });
        break;

      case 'training_completed':
        toast.success('🎓 Training Quiz Completed', {
          description: data.message || 'Worker submitted a training quiz',
          duration: 10000,
        });
        break;

      case 'cert_pending_review':
        toast.info('📄 Certificate Awaiting Review', {
          description: data.message || 'A new credential needs verification',
          duration: 10000,
        });
        break;

      case 'cert_verified':
        toast.success('✅ Certificate Verified', {
          description: data.message || 'Your certificate was verified',
          duration: 10000,
        });
        break;

      case 'cert_rejected':
        toast.error('❌ Certificate Rejected', {
          description: data.message || 'Your certificate was rejected',
          duration: 12000,
        });
        break;

      case 'shift_swap_posted':
        toast.info('🔁 Shift Swap Requested', {
          description: data.message || 'A worker requested a shift swap',
          duration: 10000,
        });
        break;

      case 'shift_swap_claimed':
        toast.info('🤝 Swap Claimed', {
          description: data.message || 'A worker claimed a swap',
          duration: 10000,
        });
        break;

      case 'shift_swap_claimed_yours':
        toast.success('🎉 Your Shift Was Picked Up', {
          description: data.message || 'Another worker claimed your shift',
          duration: 10000,
        });
        break;

      case 'shift_rejected':
        toast.warning('❌ Shift Rejected', {
          description: data.message || 'A worker rejected an assigned shift',
          duration: 12000,
        });
        break;

      case 'shift_transfer_request':
        toast.info('↔️ Transfer Request', {
          description: data.message || 'A worker wants to transfer their shift to you',
          duration: 12000,
        });
        break;

      case 'shift_transfer_requested':
        toast.info('↔️ Transfer Pending Approval', {
          description: data.message || 'A worker requested a shift transfer',
          duration: 10000,
        });
        break;

      case 'transfer_accepted':
      case 'transfer_completed':
        toast.success('✓ Transfer Completed', {
          description: data.message || 'The shift transfer was accepted',
          duration: 8000,
        });
        break;

      case 'transfer_rejected':
        toast.warning('Transfer Declined', {
          description: data.message || 'The transfer was declined',
          duration: 8000,
        });
        break;

      case 'time_edit_request':
        toast.info('🕒 Time Edit Request', {
          description: data.message || 'A worker requested a time edit',
          duration: 10000,
        });
        break;

      case 'time_edit_response':
        toast.info('🕒 Time Edit Update', {
          description: data.message || 'Your time edit was processed',
          duration: 10000,
        });
        break;

      case 'emergency_sos':
        toast.error('🚨 EMERGENCY SOS', {
          description: data.message || 'A worker triggered an emergency alert',
          duration: 30000,
        });
        playNotificationSound('alert');
        break;
      
      case 'typing':
      case 'stop_typing':
        // Don't show toast for typing indicators
        break;
      
      default:
        if (data.priority === 'high') {
          playNotificationSound('alert');
        }
        console.log('Notification:', data);
    }
  };

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user]);

  const clearNotifications = () => {
    setNotifications([]);
  };

  // Subscribe to live data_event messages. Returns an unsubscribe fn.
  const subscribeData = useCallback((listener) => {
    dataListenersRef.current.add(listener);
    return () => dataListenersRef.current.delete(listener);
  }, []);

  return (
    <WebSocketContext.Provider value={{ 
      isConnected, 
      notifications, 
      lastMessage,
      sendMessage,
      clearNotifications,
      subscribeData,
      notificationSettings,
      updateNotificationSettings
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}